// File Metadata:
// Filename: authLogger.js
// Version: 1.01
// Created: 2025-05-20
// Last Modified: 2025-06-20
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: Authentication event logging utility for debugging JWT issues

/**
 * Log authentication events for debugging purposes
 */
class AuthLogger {
  constructor() {
    this.isProduction = import.meta.env.PROD;
    this.logs = [];
    this.maxLogs = 50; // Keep last 50 auth events in memory
  }

  /**
   * Log an authentication event
   * @param {string} event - Event type
   * @param {Object} details - Event details
   */
  log(event, details = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      details,
      url: window.location.pathname,
      userAgent: navigator.userAgent.substring(0, 100) // Truncate for brevity
    };

    // Add to memory logs
    this.logs.unshift(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.pop();
    }

    // Console log for development
    if (!this.isProduction) {
      console.log(`[AUTH] ${event}:`, details);
    }

    // Send to backend for production logging (optional)
    if (this.isProduction && this.shouldSendToBackend(event)) {
      this.sendToBackend(logEntry);
    }
  }

  /**
   * Determine if event should be sent to backend
   * @param {string} event - Event type
   * @returns {boolean}
   */
  shouldSendToBackend(event) {
    const criticalEvents = [
      'token_expired',
      'token_refresh_failed',
      'socket_auth_failed',
      'user_logged_out',
      'auth_error'
    ];
    return criticalEvents.includes(event);
  }

  /**
   * Send log entry to backend (fire and forget)
   * @param {Object} logEntry - Log entry to send
   */
  async sendToBackend(logEntry) {
    try {
      await fetch('/api/auth/log-client-event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(logEntry)
      });
    } catch (error) {
      // Silently fail - we don't want logging to break the app
      console.warn('Failed to send auth log to backend:', error.message);
    }
  }

  /**
   * Get recent authentication logs
   * @param {number} count - Number of logs to return
   * @returns {Array} Recent log entries
   */
  getRecentLogs(count = 10) {
    return this.logs.slice(0, count);
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
  }

  /**
   * Export logs as JSON string
   * @returns {string} JSON representation of logs
   */
  exportLogs() {
    return JSON.stringify(this.logs, null, 2);
  }
}

// Create singleton instance
const authLogger = new AuthLogger();

// Convenience methods
export const logAuthEvent = (event, details) => authLogger.log(event, details);
export const getAuthLogs = (count) => authLogger.getRecentLogs(count);
export const clearAuthLogs = () => authLogger.clearLogs();
export const exportAuthLogs = () => authLogger.exportLogs();

export default authLogger;
